/**
 * TreatmentRequestChangeCard - Card for treatment record request entity changes.
 *
 * Displays request status, channel info, provided records, and transmission status.
 */

import { FileText, Mail, MapPin, Phone, Printer } from 'lucide-react';

import { MedicalProviderLink, TaskLink } from '@/components/entity-links/entities';
import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { formatEnumValue, formatFieldValue } from '../utils/formatting';
import { useChangedFields } from '../utils/useChangedFields';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { CollapsibleSection } from './CollapsibleSection';
import { useEntityChangeActions } from './EntityChangeActionsContext';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';

const KEY_FIELDS: FieldConfig[] = [
  { key: 'status', label: 'Status', isEnum: true },
  { key: 'is_certified', label: 'Certified' },
  { key: 'is_urgent', label: 'Urgent' },
];

interface TreatmentRequestChangeCardProps {
  entity: EntityChangeSchema;
  nested?: boolean;
  matterId?: string;
}

const ChannelDetails: React.FC<{ channel: Record<string, unknown> }> = ({
  channel,
}) => {
  const channelType = channel.type as string | undefined;
  const entries: { icon: React.ElementType; label: string; value: string }[] = [];

  if (channel.phone_number)
    entries.push({ icon: Phone, label: 'Phone', value: channel.phone_number as string });
  if (channel.fax)
    entries.push({ icon: Printer, label: 'Fax', value: channel.fax as string });
  if (channel.email)
    entries.push({ icon: Mail, label: 'Email', value: channel.email as string });
  if (channel.address)
    entries.push({ icon: MapPin, label: 'Address', value: channel.address as string });

  return (
    <div className="space-y-1">
      {channelType && (
        <div className="flex items-baseline gap-1.5 px-1 py-0.5">
          <span className="text-xs text-gray-400 shrink-0">Channel:</span>
          <Badge color="zinc" className="text-xs">
            {formatEnumValue(channelType)}
          </Badge>
        </div>
      )}
      {entries.map(({ icon: EntryIcon, label, value }) => (
        <div
          key={label}
          className="flex items-center gap-1.5 px-1 py-0.5 text-sm text-gray-700"
        >
          <EntryIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          <span className="truncate">{value}</span>
        </div>
      ))}
      {typeof channel.instructions === 'string' && channel.instructions && (
        <div className="flex items-baseline gap-1.5 px-1 py-0.5">
          <span className="text-xs text-gray-400 shrink-0">Instructions:</span>
          <span className="text-sm text-gray-700">
            {channel.instructions}
          </span>
        </div>
      )}
    </div>
  );
};

const TransmissionStatusDisplay: React.FC<{
  label: string;
  status: Record<string, unknown>;
}> = ({ label, status }) => {
  const statusValue = status.status as string | undefined;
  const sentAt = status.sent_at as string | undefined;
  const errorMessage = status.error_message as string | undefined;

  return (
    <div className="space-y-0.5 px-1 py-0.5">
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-gray-400 shrink-0">{label}:</span>
        {statusValue && (
          <Badge color="zinc" className="text-xs">
            {formatEnumValue(statusValue)}
          </Badge>
        )}
      </div>
      {sentAt && (
        <div className="text-xs text-gray-500">
          Sent: {formatFieldValue(sentAt, true)}
        </div>
      )}
      {errorMessage && (
        <div className="text-xs text-red-500">Error: {errorMessage}</div>
      )}
    </div>
  );
};

export const TreatmentRequestChangeCard: React.FC<
  TreatmentRequestChangeCardProps
> = ({ entity, nested, matterId }) => {
  const changes = useChangedFields(
    entity.change_type,
    entity.changed_fields ?? [],
  );
  const actions = useEntityChangeActions();
  const effectiveStatus =
    actions?.statusOverrides[entity.id as string] ?? entity.status;

  const snapshot =
    entity.change_type === 'deleted' ? entity.old_snapshot : entity.new_snapshot;
  const data = (snapshot?.data ?? {}) as Record<string, unknown>;
  const status = data.status as string | undefined;
  const providedRecords = (data.provided_records as string[]) ?? [];
  const channel = data.channel as Record<string, unknown> | undefined;
  const faxTransmission = data.fax_transmission_status as
    | Record<string, unknown>
    | undefined;
  const emailTransmission = data.email_transmission_status as
    | Record<string, unknown>
    | undefined;

  const name = status
    ? `Treatment Request — ${formatEnumValue(status)}`
    : 'Treatment Request';

  const shell = (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={name}
      icon={FileText}
      iconBgClassName="bg-indigo-50"
      entityStatus={effectiveStatus}
      onStatusChange={
        actions
          ? (newStatus) => actions.onStatusChange(entity.id as string, newStatus)
          : undefined
      }
      iconTextClassName="text-indigo-600"
      changeCount={changes.totalChanges}
      hasExpandedContent
      defaultExpanded={nested ? undefined : true}
    >
      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        changeType={entity.change_type}
      />

      {/* Provided records */}
      {providedRecords.length > 0 && (
        changes.isFieldChanged('provided_records') ? (
          <ChangedFieldHighlight
            label="Provided Records"
            fieldKey="provided_records"
            changes={changes}
          >
            <div className="flex gap-1 flex-wrap">
              {providedRecords.map((r) => (
                <Badge key={r} color="zinc" className="text-xs">
                  {r}
                </Badge>
              ))}
            </div>
          </ChangedFieldHighlight>
        ) : (
          <div className="flex items-center gap-1.5 px-1 py-0.5 flex-wrap">
            <span className="text-xs text-gray-400 shrink-0">Records:</span>
            {providedRecords.map((r) => (
              <Badge key={r} color="zinc" className="text-xs">
                {r}
              </Badge>
            ))}
          </div>
        )
      )}

      {/* Channel */}
      {channel && (
        <CollapsibleSection
          title="Channel"
          count={1}
          changeType={entity.change_type}
          hasChanges={changes.hasChangesUnder('channel')}
        >
          <ChannelDetails channel={channel} />
        </CollapsibleSection>
      )}

      {/* Transmission statuses */}
      {faxTransmission && (
        <TransmissionStatusDisplay
          label="Fax Transmission"
          status={faxTransmission}
        />
      )}
      {emailTransmission && (
        <TransmissionStatusDisplay
          label="Email Transmission"
          status={emailTransmission}
        />
      )}

      {/* Entity links for provider and task */}
      {matterId && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
          {typeof data.provider_id === 'string' && data.provider_id && (
            <div className="flex items-baseline gap-1.5 px-1 py-0.5">
              <span className="text-xs text-gray-400 shrink-0">Provider:</span>
              <MedicalProviderLink entityId={data.provider_id} matterId={matterId} className="text-sm font-normal">
                Provider
              </MedicalProviderLink>
            </div>
          )}
          {typeof data.task_id === 'string' && data.task_id && (
            <div className="flex items-baseline gap-1.5 px-1 py-0.5">
              <span className="text-xs text-gray-400 shrink-0">Task:</span>
              <TaskLink entityId={data.task_id} matterId={matterId} className="text-sm font-normal">
                Task
              </TaskLink>
            </div>
          )}
        </div>
      )}

      <ShowMoreSection changeType={entity.change_type}>
        <KeyFieldsGridUnchanged
          fields={KEY_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
      </ShowMoreSection>
    </EntityChangeCardShell>
  );

  return shell;
};
